import React, { createContext, useContext, useMemo } from "react";

export type LanguageCode = "en" | "yo" | "ig" | "fr" | "ha" | "ng";

type Translations = Record<LanguageCode, Record<string, string>>;

const translations: Translations = {
  en: {
    "app.name": "Swifna",
    "nav.ask_ai": "Ask AI",
    "nav.buy": "Buy",
    "nav.history": "History",
    "nav.me": "Me",
    "lang.english": "English",
    "lang.yoruba": "Yoruba",
    "lang.igbo": "Igbo",
    "lang.french": "French",
    "lang.hausa": "Hausa",
    "lang.pidgin": "Pidgin",

    "common.back": "Back",
    "common.balance": "Balance",
    "common.pay": "Pay",
    "common.save": "Save",
    "common.share": "Share",
    "common.date": "Date",
    "common.service": "Service",
    "common.ref_id": "Ref ID",
    "common.desc": "Desc",
    "common.token": "Token",
    "common.pin": "PIN",
    "common.success": "Success",
    "common.failed": "Failed",
    "common.pending": "Pending",
    "common.cancel": "Cancel",
    "common.close": "Close",
    "common.search": "Search",
    "common.copy": "Copy",
    "common.download": "Download",
    "common.amount": "Amount",
    "common.select": "Select",
    "common.recent": "Recent",
    "common.my_number": "My Number",
    "common.no_recent_transactions": "No recent transactions",
    "common.no_recent_activity": "No recent activity",
    "common.loading": "Loading",
    "common.save_image": "Save Image",
    "common.save_pdf": "Save PDF",
    "common.pay_securely": "Pay Securely",

    "dashboard.available_balance": "Available Balance",
    "dashboard.fund": "Fund",
    "dashboard.withdraw": "Withdraw",
    "dashboard.airtime": "Airtime",
    "dashboard.data": "Data",
    "dashboard.cable": "Cable",
    "dashboard.electricity": "Electricity",
    "dashboard.exam": "Exam",
    "dashboard.recharge_pin": "Recharge Pin",
    "dashboard.airtime_to_cash": "Airtime to Cash",
    "dashboard.swap_airtime_for_cash": "Swap Airtime for Cash",
    "dashboard.recent_activity": "Recent Activity",
    "dashboard.fund_wallet": "Fund Wallet",
    "dashboard.total_amount": "Total Amount",
    "dashboard.generated_by": "Generated by NaijaPay",
    "dashboard.reference_copied": "Reference copied!",
    "dashboard.receipt_title": "Transaction Receipt",
    "dashboard.receipt_for": "Receipt for {type} - ₦{amount}",
    "dashboard.error_generating_pdf": "Error generating PDF",
    "dashboard.min_deposit": "Min ₦100",
    "dashboard.wallet_funded": "Wallet Funded Successfully!",

    "airtime.mobile_number": "Mobile Number",
    "airtime.select_recent": "Select Recent",
    "airtime.recent_transaction": "Recent Transaction",
    "airtime.top_up": "Top up",
    "airtime.cashback": "Cashback",
    "airtime.pay_amount": "Pay ₦{amount}",
    "airtime.custom_amount": "Or enter custom amount (₦50 - ₦500,000)",
    "airtime.insufficient_balance": "Insufficient balance",
    "airtime.sent_success": "Airtime Sent Successfully!",
    "airtime.failed": "Failed to process airtime",

    "data.select_recent": "Select Recent",
    "data.recent_data": "Recent Data",
    "data.no_plans": "No plans found for this category.",
    "data.price": "Price",
    "data.buy_bundle": "Buy Bundle",
    "data.insufficient_balance": "Insufficient Balance",
    "data.success_sent": "Success! {plan} sent.",
    "data.transaction_failed": "Transaction Failed",

    "assistant.title": "AI Assistant",
    "assistant.subtitle": "Always Online",
    "assistant.greeting": "Hi {name}! 👋 I'm your Swifna AI. Ask me about your balance, funding your wallet, or how to buy data!",
    "assistant.input_placeholder": "Ask me anything...",
    "assistant.quick.balance": "Check my balance",
    "assistant.quick.deposit": "How do I deposit?",
    "assistant.quick.buy_data": "Buy Data",
    "assistant.quick.who": "Who are you?",
    "assistant.offline": "I'm having trouble connecting right now. Please try again.",

    "data.tab.hot": "Hot",
    "data.tab.daily": "Daily",
    "data.tab.weekly": "Weekly",
    "data.tab.monthly": "Monthly",
    "data.type.all": "All",
    "data.type.sme": "SME",
    "data.type.cg": "CG",
    "data.type.gifting": "Gifting",

    "history.share_not_supported": "Sharing isn't supported in this browser. Downloading instead.",
    "history.reference_copied_clipboard": "Reference copied to clipboard!",
    "history.receipt": "RECEIPT",
    "history.pin_token": "PIN / TOKEN",
    "history.generated_receipt": "Generated Receipt",
    "history.loading": "Loading History...",
    "history.activity": "Activity",
    "history.financial_overview": "Your Financial Overview",
    "history.total_spent": "Total Spent",
    "history.lifetime_spending": "Lifetime spending",
    "history.transactions": "Transactions",
    "history.success_rate": "Success Rate",
    "history.spending_analysis": "Spending Analysis",
    "history.top": "Top",
    "history.na": "N/A",
    "history.no_data": "No data yet",
    "history.search_transactions": "Search transactions...",
    "history.filter.all": "All",
    "history.filter.deposit": "Deposit",
    "history.no_transactions_found": "No Transactions Found",

    "auth.alert.account_created": "Account created! Please check your email to confirm your account.",
    "auth.alert.reset_link_sent": "Password reset link sent! Check your email.",
    "auth.alert.generic_error": "Something went wrong.",
    "auth.title.login": "Welcome Back",
    "auth.title.signup": "Join the Family",
    "auth.title.reset": "Reset Password",
    "auth.button.sign_in": "Sign In",
    "auth.button.create_account": "Create Free Account",
    "auth.button.send_reset": "Send Reset Link",
    "auth.swipe_explore": "Swipe to explore",
    "auth.secure_access": "Secure Access",
    "auth.reset_hint": "Enter your email to receive a password reset link.",
    "auth.full_name": "Full Name",
    "auth.phone": "Phone Number",
    "auth.preferred_language": "Preferred Language",
    "auth.email": "Email Address",
    "auth.password": "Password",
    "auth.forgot_password": "Forgot Password?",
    "auth.back_to_login": "Back to Login",
    "auth.switch_to_signup": "New here? Open an account",
    "auth.switch_to_login": "Already have an account? Log in",
    "auth.bank_grade_security": "Bank-Grade Security",

    "profile.updated": "Profile Updated!",
    "profile.update_failed": "Update failed. Check connection.",
    "profile.fill_all_fields": "Please fill all fields",
    "profile.passwords_no_match": "New passwords do not match",
    "profile.password_min": "Password must be at least 6 chars",
    "profile.incorrect_current_password": "Incorrect Current Password",
    "profile.password_updated": "Password Updated Successfully!",
    "profile.password_update_failed": "Failed to update password",
    "profile.personal_details": "Personal Details",
    "profile.name_phone": "Name & Phone",
    "profile.phone_number": "Phone Number",
    "profile.save_changes": "Save Changes",
    "profile.appearance": "Appearance",
    "profile.dark_mode": "Dark Mode",
    "profile.light_mode": "Light Mode",
    "profile.security": "Security",
    "profile.change_password": "Change Password",
    "profile.current_password": "Current Password",
    "profile.new_password": "New Password",
    "profile.confirm_new_password": "Confirm New Password",
    "profile.update_password": "Update Password",
    "profile.help_support": "Help & Support",
    "profile.contact_us": "Contact Us",
    "profile.log_out": "Log Out",

    "exam.verify_profile_id": "Verify Profile ID",
    "exam.purchase_success": "Purchase Successful! Check Receipt for PIN.",
    "exam.purchase_failed": "Purchase Failed",
    "exam.select_exam": "Select Exam",
    "exam.profile_id": "Profile ID",
    "exam.enter_profile_id": "Enter 10-digit ID",
    "exam.exam_type": "Exam Type",
    "exam.quantity": "Quantity",
    "exam.pins": "PINs",
    "exam.purchase": "Purchase",
    "exam.verifying": "Verifying...",
    "exam.verified_id": "Verified ID",
    "exam.invalid_profile_id": "Invalid Profile ID",
    "exam.verification_failed": "Verification Failed",

    "airtime_to_cash.proceed_manual": "Proceed to transfer airtime manually to the admin number provided below.",
    "airtime_to_cash.failed_initiate": "Failed to initiate",
    "airtime_to_cash.request_initiated": "Request Initiated",
    "airtime_to_cash.complete_transfer": "Please complete the transfer to finalize your cash request.",
    "airtime_to_cash.instruction": "Instruction",
    "airtime_to_cash.you_sent": "You Sent",
    "airtime_to_cash.you_receive": "You Receive",
    "airtime_to_cash.i_transferred": "I Have Transferred",
    "airtime_to_cash.swap_airtime": "Swap Airtime",
    "airtime_to_cash.sender_mobile": "Sender Mobile Number",
    "airtime_to_cash.conversion_details": "Conversion Details",
    "airtime_to_cash.amount_to_sell": "Amount to Sell",
    "airtime_to_cash.rate_80": "80% Rate",
    "airtime_to_cash.funds_after_verification": "Funds added to wallet instantly after verification.",
    "airtime_to_cash.note_manual_transfer": "Note: The transfer is manual. You will be given an admin number to transfer the airtime to in the next step.",
    "airtime_to_cash.proceed_swap": "Proceed to Swap",
  },
  yo: {
    "app.name": "Swifna",
    "nav.ask_ai": "Beere AI",
    "nav.buy": "Ra",
    "nav.history": "Itan",
    "nav.me": "Emi",
    "lang.english": "Gẹ̀ẹ́sì",
    "lang.yoruba": "Yorùbá",
    "lang.igbo": "Igbo",
    "lang.french": "Faransé",
    "lang.hausa": "Hausa",
    "lang.pidgin": "Pidgin",

    "common.back": "Pada",
    "common.balance": "Ìyókù",
    "common.pay": "Sanwo",
    "common.save": "Fipamọ",
    "common.share": "Pín",
    "common.date": "Ọjọ́",
    "common.service": "Iṣẹ́",
    "common.ref_id": "ID ìtọ́kasí",
    "common.desc": "Apejuwe",
    "common.token": "Tókeni",
    "common.pin": "PIN",
    "common.success": "Aseyọrí",
    "common.failed": "Kuna",
    "common.pending": "Duro",
    "common.cancel": "Fagilé",
    "common.close": "Pa",
    "common.search": "Wá",
    "common.copy": "Dàkọ",
    "common.download": "Gba silẹ",
    "common.amount": "Owo",
    "common.select": "Yan",
    "common.recent": "Tó ṣẹṣẹ",
    "common.my_number": "Nọ́mbà mi",
    "common.no_recent_transactions": "Kò sí ìdunadura tuntun",
    "common.no_recent_activity": "Kò sí iṣẹ́ tuntun",
    "common.loading": "Ń ṣiṣẹ́",
    "common.save_image": "Fipamọ Aworan",
    "common.save_pdf": "Fipamọ PDF",
    "common.pay_securely": "Sanwo Ní Aàbò",

    "dashboard.available_balance": "Ìyókù Tó Wà",
    "dashboard.fund": "Fún",
    "dashboard.withdraw": "Yọ́",
    "dashboard.airtime": "Airtime",
    "dashboard.data": "Data",
    "dashboard.cable": "Kébù",
    "dashboard.electricity": "Ẹlẹ́tríkì",
    "dashboard.exam": "Ìdánwò",
    "dashboard.recharge_pin": "PIN Ríṣàjì",
    "dashboard.airtime_to_cash": "Airtime sí Owó",
    "dashboard.swap_airtime_for_cash": "Yí Airtime sí Owó",
    "dashboard.recent_activity": "Ìṣe Tó ṣẹṣẹ",
    "dashboard.fund_wallet": "Fún Wọ́létì",
    "dashboard.total_amount": "Gbogbo Owo",
    "dashboard.generated_by": "Ṣe nipasẹ NaijaPay",
    "dashboard.reference_copied": "Ìtọ́kasí ti dàkọ!",
    "dashboard.receipt_title": "Ìríṣìí Ìdunadura",
    "dashboard.receipt_for": "Ìríṣìí fún {type} - ₦{amount}",
    "dashboard.error_generating_pdf": "Aṣìṣe nígbà ṣíṣe PDF",
    "dashboard.min_deposit": "Kéré jù ₦100",
    "dashboard.wallet_funded": "A ti kún Wọ́létì!",

    "airtime.mobile_number": "Nọ́mbà Fóònù",
    "airtime.select_recent": "Yan Tó ṣẹṣẹ",
    "airtime.recent_transaction": "Ìdunadura Tó ṣẹṣẹ",
    "airtime.top_up": "Tóòpù",
    "airtime.cashback": "Padà Owo",
    "airtime.pay_amount": "San ₦{amount}",
    "airtime.custom_amount": "Tabi tẹ owó míì (₦50 - ₦500,000)",
    "airtime.insufficient_balance": "Ìyókù kò tó",
    "airtime.sent_success": "A fi Airtime ranṣẹ́!",
    "airtime.failed": "Kò le ṣe Airtime",

    "data.select_recent": "Yan Tó ṣẹṣẹ",
    "data.recent_data": "Data Tó ṣẹṣẹ",
    "data.no_plans": "Kò sí àtòjọ fún ẹ̀ka yìí.",
    "data.price": "Iye",
    "data.buy_bundle": "Ra Bándùùlù",
    "data.insufficient_balance": "Ìyókù kò tó",
    "data.success_sent": "Aseyọrí! {plan} ti rán.",
    "data.transaction_failed": "Ìdunadura Kùnà",

    "assistant.title": "Olùrànlọ́wọ́ AI",
    "assistant.subtitle": "Ní Lọ́ọ̀nì Gbogbo",
    "assistant.greeting": "Báwo {name}! 👋 Mo jẹ AI Swifna rẹ. Béèrè nípa ìyókù, bí o ṣe lè kún wọ́létì, tàbí bí o ṣe lè ra data!",
    "assistant.input_placeholder": "Béèrè ohunkóhun...",
    "assistant.quick.balance": "Ṣàyẹ̀wò ìyókù mi",
    "assistant.quick.deposit": "Báwo ni mo ṣe kún?",
    "assistant.quick.buy_data": "Ra Data",
    "assistant.quick.who": "Ta ni ẹ?",
    "assistant.offline": "Mo ní ìṣòro ìbánisọ̀rọ̀. Jọ̀wọ́ gbìyànjú lẹ́ẹ̀kansi.",

    "data.tab.hot": "Gbona",
    "data.tab.daily": "Ojoojúmọ́",
    "data.tab.weekly": "Ọ̀sẹ̀",
    "data.tab.monthly": "Oṣù",
    "data.type.all": "Gbogbo",
    "data.type.sme": "SME",
    "data.type.cg": "CG",
    "data.type.gifting": "Fífún",

    "history.share_not_supported": "Pín kò ṣiṣẹ́ lórí aṣàwákiri yìí. A ń gba silẹ dipo.",
    "history.reference_copied_clipboard": "Ìtọ́kasí ti dàkọ sí clipboard!",
    "history.receipt": "ÌRÍṢÌÍ",
    "history.pin_token": "PIN / TÓKÉN",
    "history.generated_receipt": "Ìríṣìí tí a ṣe",
    "history.loading": "Ń kó ìtàn...",
    "history.activity": "Ìṣe",
    "history.financial_overview": "Àkótán Ìṣúná Rẹ",
    "history.total_spent": "Gbogbo Owo Tó Ná",
    "history.lifetime_spending": "Owo tí o ti ná lapapọ",
    "history.transactions": "Ìdunadura",
    "history.success_rate": "Oṣuwọn Aseyọrí",
    "history.spending_analysis": "Ìtúpalẹ̀ Inawo",
    "history.top": "Gíga",
    "history.na": "Kò sí",
    "history.no_data": "Ko sí data",
    "history.search_transactions": "Ṣàwárí ìdunadura...",
    "history.filter.all": "Gbogbo",
    "history.filter.deposit": "Ìfikún",
    "history.no_transactions_found": "Kò rí ìdunadura",

    "auth.alert.account_created": "A dá àkànṣe! Jọwọ ṣàyẹwo imeeli rẹ lati jẹ́rìí.",
    "auth.alert.reset_link_sent": "A rán linki àtúnṣe ọrọigbaniwọle. Ṣàyẹwo imeeli.",
    "auth.alert.generic_error": "Nkankan lọ̀ọ́ jẹ.",
    "auth.title.login": "Káabọ̀ Padà",
    "auth.title.signup": "Darapọ̀ mọ́ wa",
    "auth.title.reset": "Tún Ọrọigbaniwọle Ṣe",
    "auth.button.sign_in": "Wọlé",
    "auth.button.create_account": "Dá Àkànṣe Ọfẹ́",
    "auth.button.send_reset": "Rán Linki Àtúnṣe",
    "auth.swipe_explore": "Fa láti ṣàwárí",
    "auth.secure_access": "Ìwọlé Ààbò",
    "auth.reset_hint": "Tẹ imeeli rẹ lati gba linki àtúnṣe.",
    "auth.full_name": "Orúkọ Kún",
    "auth.email": "Imeeli",
    "auth.password": "Ọrọigbaniwọle",
    "auth.forgot_password": "Gbàgbé Ọrọigbaniwọle?",
    "auth.back_to_login": "Pada sí Wọlé",
    "auth.switch_to_signup": "Titun nibi? Ṣí àkànṣe",
    "auth.switch_to_login": "Ní àkànṣe tẹlẹ? Wọlé",
    "auth.bank_grade_security": "Ààbò Ilé-ifowopamọ",

    "profile.updated": "A ti ṣe imudojuiwọn!",
    "profile.update_failed": "Imudojuiwọn kuna. Ṣàyẹwo asopọ.",
    "profile.fill_all_fields": "Jọwọ kún gbogbo aaye",
    "profile.passwords_no_match": "Ọrọigbaniwọle tuntun ko ba ara wọn mu",
    "profile.password_min": "Ọrọigbaniwọle gbọdọ jẹ o kere ju 6",
    "profile.incorrect_current_password": "Ọrọigbaniwọle lọwọlọwọ kò tọ",
    "profile.password_updated": "Ọrọigbaniwọle ti yipada!",
    "profile.password_update_failed": "Ko le ṣe imudojuiwọn ọrọigbaniwọle",
    "profile.personal_details": "Àlàyé Ti Ara",
    "profile.name_phone": "Orúkọ & Fóònù",
    "profile.phone_number": "Nọ́mbà Fóònù",
    "profile.save_changes": "Fipamọ Ayipada",
    "profile.appearance": "Ìfarahàn",
    "profile.dark_mode": "Ìpo Dúdú",
    "profile.light_mode": "Ìpo Imọlẹ",
    "profile.security": "Ààbò",
    "profile.change_password": "Yí Ọrọigbaniwọle",
    "profile.current_password": "Ọrọigbaniwọle Lọwọlọwọ",
    "profile.new_password": "Ọrọigbaniwọle Tuntun",
    "profile.confirm_new_password": "Jẹrìí Ọrọigbaniwọle Tuntun",
    "profile.update_password": "Ṣe Imudojuiwọn Ọrọigbaniwọle",
    "profile.help_support": "Ìrànlọ́wọ́ & Atilẹ́yìn",
    "profile.contact_us": "Kan Sí Wa",
    "profile.log_out": "Jade",

    "exam.verify_profile_id": "Jẹrìí ID Profaili",
    "exam.purchase_success": "Rira aṣeyọrí! Ṣàyẹwo ìríṣìí fún PIN.",
    "exam.purchase_failed": "Rira kuna",
    "exam.select_exam": "Yan Ìdánwò",
    "exam.profile_id": "ID Profaili",
    "exam.enter_profile_id": "Tẹ ID díjítì 10",
    "exam.exam_type": "Iru Ìdánwò",
    "exam.quantity": "Iye",
    "exam.pins": "PINs",
    "exam.purchase": "Ra",
    "exam.verifying": "Ń jẹrìí...",
    "exam.verified_id": "ID ti jẹrìí",
    "exam.invalid_profile_id": "ID Profaili kò tọ",
    "exam.verification_failed": "Ìjẹrìí kuna",

    "airtime_to_cash.proceed_manual": "Tẹsiwaju kí o rán airtime lọ́wọ́ sí nọmba admin ni isalẹ.",
    "airtime_to_cash.failed_initiate": "Kò le bẹ̀rẹ̀",
    "airtime_to_cash.request_initiated": "Ìbéèrè ti bẹ̀rẹ̀",
    "airtime_to_cash.complete_transfer": "Jọwọ parí ìrànṣẹ́ láti parí ìbéèrè owó rẹ.",
    "airtime_to_cash.instruction": "Ìtọ́nisọ́nà",
    "airtime_to_cash.you_sent": "O Rán",
    "airtime_to_cash.you_receive": "O Gba",
    "airtime_to_cash.i_transferred": "Mo Ti Rán",
    "airtime_to_cash.swap_airtime": "Yí Airtime",
    "airtime_to_cash.sender_mobile": "Nọ́mbà Fóònù Olurán",
    "airtime_to_cash.conversion_details": "Àlàyé Àtúnṣe",
    "airtime_to_cash.amount_to_sell": "Owo Tó fẹ́ Ta",
    "airtime_to_cash.rate_80": "Oṣuwọn 80%",
    "airtime_to_cash.funds_after_verification": "Owo wọ̀lé lẹ́sẹ̀kẹsẹ̀ lẹ́yìn ìmúlẹ̀.",
    "airtime_to_cash.note_manual_transfer": "Akiyesi: ìrànṣẹ́ jẹ ọwọ́. A ó fún ọ ní nọmba admin.",
    "airtime_to_cash.proceed_swap": "Tẹsiwaju Láti Yí",
  },
  ig: {
    "app.name": "Swifna",
    "nav.ask_ai": "Jụọ AI",
    "nav.buy": "Zụta",
    "nav.history": "Akụkọ",
    "nav.me": "M",
    "lang.english": "Bekee",
    "lang.yoruba": "Yorùbá",
    "lang.igbo": "Igbo",
    "lang.french": "French",
    "lang.hausa": "Hausa",
    "lang.pidgin": "Pidgin",

    "common.back": "Laghachi",
    "common.balance": "Ntụnye",
    "common.pay": "Kwụọ",
    "common.save": "Chekwaa",
    "common.share": "Kekọrịta",
    "common.date": "Ụbọchị",
    "common.service": "Ọrụ",
    "common.ref_id": "ID ntụaka",
    "common.desc": "Nkọwa",
    "common.token": "Tọken",
    "common.pin": "PIN",
    "common.success": "Gara nke ọma",
    "common.failed": "Dara",
    "common.pending": "Na-echere",
    "common.cancel": "Kagbuo",
    "common.close": "Mechie",
    "common.search": "Chọọ",
    "common.copy": "Detuo",
    "common.download": "Budata",
    "common.amount": "Ego",
    "common.select": "Họrọ",
    "common.recent": "N’oge ọhụrụ",
    "common.my_number": "Nọmba m",
    "common.no_recent_transactions": "Enweghị azụmahịa ọhụrụ",
    "common.no_recent_activity": "Enweghị ọrụ ọhụrụ",
    "common.loading": "Na-ebubata",
    "common.save_image": "Chekwaa Foto",
    "common.save_pdf": "Chekwaa PDF",
    "common.pay_securely": "Kwụọ Nke Ọma",

    "dashboard.available_balance": "Ntụnye Dị",
    "dashboard.fund": "Tinye",
    "dashboard.withdraw": "Wepụ",
    "dashboard.airtime": "Airtime",
    "dashboard.data": "Data",
    "dashboard.cable": "Cable",
    "dashboard.electricity": "Ọkụ",
    "dashboard.exam": "Ule",
    "dashboard.recharge_pin": "PIN Recharge",
    "dashboard.airtime_to_cash": "Airtime gaa Ego",
    "dashboard.swap_airtime_for_cash": "Gbanwee Airtime gaa Ego",
    "dashboard.recent_activity": "Ọrụ Ọhụrụ",
    "dashboard.fund_wallet": "Tinye Wallet",
    "dashboard.total_amount": "Ego Niile",
    "dashboard.generated_by": "NaijaPay mepụtara",
    "dashboard.reference_copied": "Detụla ntụaka!",
    "dashboard.receipt_title": "Akwụkwọ azụmahịa",
    "dashboard.receipt_for": "Akwụkwọ maka {type} - ₦{amount}",
    "dashboard.error_generating_pdf": "Njehie n’ịmepụta PDF",
    "dashboard.min_deposit": "Obere ₦100",
    "dashboard.wallet_funded": "Akwunyere Wallet nke ọma!",

    "airtime.mobile_number": "Nọmba ekwentị",
    "airtime.select_recent": "Họrọ nke ọhụrụ",
    "airtime.recent_transaction": "Azụmahịa ọhụrụ",
    "airtime.top_up": "Tinye",
    "airtime.cashback": "Nlaghachi ego",
    "airtime.pay_amount": "Kwụọ ₦{amount}",
    "airtime.custom_amount": "Ma tinye ego ọzọ (₦50 - ₦500,000)",
    "airtime.insufficient_balance": "Ntụnye ezughi",
    "airtime.sent_success": "E zitere Airtime nke ọma!",
    "airtime.failed": "Enweghị ike ime Airtime",

    "data.select_recent": "Họrọ nke ọhụrụ",
    "data.recent_data": "Data ọhụrụ",
    "data.no_plans": "Enweghị atụmatụ n’otu a.",
    "data.price": "Ahịa",
    "data.buy_bundle": "Zụta Bándụl",
    "data.insufficient_balance": "Ntụnye ezughi",
    "data.success_sent": "Gara nke ọma! {plan} ezitere.",
    "data.transaction_failed": "Azụmahịa dara",

    "assistant.title": "Onye Enyemaka AI",
    "assistant.subtitle": "N’ịntanetị Mgbe niile",
    "assistant.greeting": "Ndewo {name}! 👋 Abụ m AI Swifna gị. Jụọ m maka ntụnye, ịtinye wallet, ma ọ bụ otu esi azụta data!",
    "assistant.input_placeholder": "Jụọ ihe ọ bụla...",
    "assistant.quick.balance": "Lelee ntụnye m",
    "assistant.quick.deposit": "Kedu ka m ga-esi tinye?",
    "assistant.quick.buy_data": "Zụta Data",
    "assistant.quick.who": "Onye ị bụ?",
    "assistant.offline": "Enweghị m ike ijikọ ugbu a. Biko nwalee ọzọ.",

    "data.tab.hot": "Hot",
    "data.tab.daily": "Kwa ụbọchị",
    "data.tab.weekly": "Kwa izu",
    "data.tab.monthly": "Kwa ọnwa",
    "data.type.all": "Niile",
    "data.type.sme": "SME",
    "data.type.cg": "CG",
    "data.type.gifting": "Inye",

    "history.share_not_supported": "Ịkekọrịta adịghị arụ ọrụ na brawza a. A na-ebudata kama.",
    "history.reference_copied_clipboard": "E detụrụ ntụaka na clipboard!",
    "history.receipt": "NKWỤGHACHI",
    "history.pin_token": "PIN / TOKEN",
    "history.generated_receipt": "Nkwụghachi e kere",
    "history.loading": "Na-ebubata akụkọ...",
    "history.activity": "Ọrụ",
    "history.financial_overview": "Nchịkọta ego gị",
    "history.total_spent": "Ego niile e liri",
    "history.lifetime_spending": "Ego ndụ niile",
    "history.transactions": "Azụmahịa",
    "history.success_rate": "Ọnụego ihe ịga nke ọma",
    "history.spending_analysis": "Nyocha mmefu",
    "history.top": "Elu",
    "history.na": "N/A",
    "history.no_data": "Enweghị data",
    "history.search_transactions": "Chọọ azụmahịa...",
    "history.filter.all": "Niile",
    "history.filter.deposit": "Ntinye",
    "history.no_transactions_found": "A hụghị azụmahịa",

    "auth.alert.account_created": "A mepụtara akaụntụ! Biko lelee email gị ka ịkwado.",
    "auth.alert.reset_link_sent": "E zitere njikọ ịtọgharịa okwu nzuzo. Lelee email gị.",
    "auth.alert.generic_error": "Ihe mebiri.",
    "auth.title.login": "Nabata azụ",
    "auth.title.signup": "Soro anyị",
    "auth.title.reset": "Tọgharịa Okwu Nzuzo",
    "auth.button.sign_in": "Banye",
    "auth.button.create_account": "Mepụta Akaụntụ Efú",
    "auth.button.send_reset": "Ziga Njikọ Ntọgharị",
    "auth.swipe_explore": "Kpụpụta ka ị nyochaa",
    "auth.secure_access": "Nzọụkwụ Nche",
    "auth.reset_hint": "Tinye email gị ka ị nweta njikọ ntọgharị.",
    "auth.full_name": "Aha zuru ezu",
    "auth.email": "Email",
    "auth.password": "Okwu Nzuzo",
    "auth.forgot_password": "Chefuru Okwu Nzuzo?",
    "auth.back_to_login": "Laghachi n'ịbanye",
    "auth.switch_to_signup": "Ọ bụ ọhụrụ? Mepee akaụntụ",
    "auth.switch_to_login": "Ị nwere akaụntụ? Banye",
    "auth.bank_grade_security": "Nchekwa ụlọ akụ",

    "profile.updated": "Emelitere profaịlụ!",
    "profile.update_failed": "Mmelite dara. Lelee njikọ.",
    "profile.fill_all_fields": "Biko juo ebe niile",
    "profile.passwords_no_match": "Okwu nzuzo ọhụrụ adịghị dakọ",
    "profile.password_min": "Okwu nzuzo kwesịrị ịdị ma ọ dịkarịa ala 6",
    "profile.incorrect_current_password": "Okwu nzuzo ugbu a ezighi ezi",
    "profile.password_updated": "Okwu nzuzo emelitere!",
    "profile.password_update_failed": "Enweghị ike imelite okwu nzuzo",
    "profile.personal_details": "Nkọwa nkeonwe",
    "profile.name_phone": "Aha & Ekwentị",
    "profile.phone_number": "Nọmba ekwentị",
    "profile.save_changes": "Chekwaa mgbanwe",
    "profile.appearance": "Ịdị anya",
    "profile.dark_mode": "Ọnọdụ ọchịchịrị",
    "profile.light_mode": "Ọnọdụ ọkụ",
    "profile.security": "Nche",
    "profile.change_password": "Gbanwee okwu nzuzo",
    "profile.current_password": "Okwu nzuzo ugbu a",
    "profile.new_password": "Okwu nzuzo ọhụrụ",
    "profile.confirm_new_password": "Kwenye okwu nzuzo ọhụrụ",
    "profile.update_password": "Melite okwu nzuzo",
    "profile.help_support": "Enyemaka & Nkwado",
    "profile.contact_us": "Kpọtụrụ anyị",
    "profile.log_out": "Pụọ",

    "exam.verify_profile_id": "Nyochaa ID Profailị",
    "exam.purchase_success": "Ịzụta rụpụtara! Lelee nkwụghachi maka PIN.",
    "exam.purchase_failed": "Ịzụta dara",
    "exam.select_exam": "Họrọ ule",
    "exam.profile_id": "ID Profailị",
    "exam.enter_profile_id": "Tinye ID nọmba 10",
    "exam.exam_type": "Ụdị ule",
    "exam.quantity": "Ọnụ ọgụgụ",
    "exam.pins": "PINs",
    "exam.purchase": "Zụta",
    "exam.verifying": "Na-enyocha...",
    "exam.verified_id": "ID a nyochara",
    "exam.invalid_profile_id": "ID profailị ezighi ezi",
    "exam.verification_failed": "Nyocha dara",

    "airtime_to_cash.proceed_manual": "Gaa n'ihu zipu airtime n'aka admin n'okpuru.",
    "airtime_to_cash.failed_initiate": "Enweghị ike ibido",
    "airtime_to_cash.request_initiated": "A malitere arịrịọ",
    "airtime_to_cash.complete_transfer": "Biko mechaa nnyefe ka arịrịọ ego gị kwụsị.",
    "airtime_to_cash.instruction": "Ntuziaka",
    "airtime_to_cash.you_sent": "Ị zigara",
    "airtime_to_cash.you_receive": "Ị na-anata",
    "airtime_to_cash.i_transferred": "Agafela m",
    "airtime_to_cash.swap_airtime": "Gbanwee Airtime",
    "airtime_to_cash.sender_mobile": "Nọmba ekwentị onye zipu",
    "airtime_to_cash.conversion_details": "Nkọwa ntụgharị",
    "airtime_to_cash.amount_to_sell": "Ego ị ga-ere",
    "airtime_to_cash.rate_80": "Ọnụego 80%",
    "airtime_to_cash.funds_after_verification": "A ga-etinye ego na wallet ozugbo e nyochara.",
    "airtime_to_cash.note_manual_transfer": "Cheta: nnyefe bụ aka. A ga-enye gị nọmba admin.",
    "airtime_to_cash.proceed_swap": "Gaa n'ihu gbanwee",
  },
  fr: {
    "app.name": "Swifna",
    "nav.ask_ai": "Demander à l’IA",
    "nav.buy": "Acheter",
    "nav.history": "Historique",
    "nav.me": "Moi",
    "lang.english": "Anglais",
    "lang.yoruba": "Yoruba",
    "lang.igbo": "Igbo",
    "lang.french": "Français",
    "lang.hausa": "Haoussa",
    "lang.pidgin": "Pidgin",

    "common.back": "Retour",
    "common.balance": "Solde",
    "common.pay": "Payer",
    "common.save": "Enregistrer",
    "common.share": "Partager",
    "common.date": "Date",
    "common.service": "Service",
    "common.ref_id": "ID Réf.",
    "common.desc": "Desc",
    "common.token": "Jeton",
    "common.pin": "PIN",
    "common.success": "Succès",
    "common.failed": "Échec",
    "common.pending": "En attente",
    "common.cancel": "Annuler",
    "common.close": "Fermer",
    "common.search": "Rechercher",
    "common.copy": "Copier",
    "common.download": "Télécharger",
    "common.amount": "Montant",
    "common.select": "Choisir",
    "common.recent": "Récent",
    "common.my_number": "Mon numéro",
    "common.no_recent_transactions": "Aucune transaction récente",
    "common.no_recent_activity": "Aucune activité récente",
    "common.loading": "Chargement",
    "common.save_image": "Enregistrer l’image",
    "common.save_pdf": "Enregistrer PDF",
    "common.pay_securely": "Payer en sécurité",

    "dashboard.available_balance": "Solde disponible",
    "dashboard.fund": "Approvisionner",
    "dashboard.withdraw": "Retirer",
    "dashboard.airtime": "Airtime",
    "dashboard.data": "Données",
    "dashboard.cable": "Câble",
    "dashboard.electricity": "Électricité",
    "dashboard.exam": "Examen",
    "dashboard.recharge_pin": "PIN Recharge",
    "dashboard.airtime_to_cash": "Airtime en cash",
    "dashboard.swap_airtime_for_cash": "Échanger Airtime contre cash",
    "dashboard.recent_activity": "Activité récente",
    "dashboard.fund_wallet": "Approvisionner le portefeuille",
    "dashboard.total_amount": "Montant total",
    "dashboard.generated_by": "Généré par NaijaPay",
    "dashboard.reference_copied": "Référence copiée !",
    "dashboard.receipt_title": "Reçu de transaction",
    "dashboard.receipt_for": "Reçu pour {type} - ₦{amount}",
    "dashboard.error_generating_pdf": "Erreur lors de la génération du PDF",
    "dashboard.min_deposit": "Min ₦100",
    "dashboard.wallet_funded": "Portefeuille approvisionné !",

    "airtime.mobile_number": "Numéro mobile",
    "airtime.select_recent": "Choisir récent",
    "airtime.recent_transaction": "Transaction récente",
    "airtime.top_up": "Recharger",
    "airtime.cashback": "Cashback",
    "airtime.pay_amount": "Payer ₦{amount}",
    "airtime.custom_amount": "Ou saisir un montant (₦50 - ₦500,000)",
    "airtime.insufficient_balance": "Solde insuffisant",
    "airtime.sent_success": "Airtime envoyé avec succès !",
    "airtime.failed": "Impossible de traiter l’airtime",

    "data.select_recent": "Choisir récent",
    "data.recent_data": "Données récentes",
    "data.no_plans": "Aucun forfait pour cette catégorie.",
    "data.price": "Prix",
    "data.buy_bundle": "Acheter un forfait",
    "data.insufficient_balance": "Solde insuffisant",
    "data.success_sent": "Succès ! {plan} envoyé.",
    "data.transaction_failed": "Transaction échouée",

    "assistant.title": "Assistant IA",
    "assistant.subtitle": "Toujours en ligne",
    "assistant.greeting": "Salut {name} ! 👋 Je suis votre IA Swifna. Demandez-moi votre solde, comment approvisionner votre portefeuille ou acheter des données !",
    "assistant.input_placeholder": "Demandez-moi n’importe quoi...",
    "assistant.quick.balance": "Vérifier mon solde",
    "assistant.quick.deposit": "Comment déposer ?",
    "assistant.quick.buy_data": "Acheter des données",
    "assistant.quick.who": "Qui êtes-vous ?",
    "assistant.offline": "Je n’arrive pas à me connecter. Réessayez.",

    "data.tab.hot": "Populaire",
    "data.tab.daily": "Quotidien",
    "data.tab.weekly": "Hebdo",
    "data.tab.monthly": "Mensuel",
    "data.type.all": "Tous",
    "data.type.sme": "SME",
    "data.type.cg": "CG",
    "data.type.gifting": "Cadeau",

    "history.share_not_supported": "Le partage n’est pas pris en charge dans ce navigateur. Téléchargement à la place.",
    "history.reference_copied_clipboard": "Référence copiée dans le presse‑papiers !",
    "history.receipt": "REÇU",
    "history.pin_token": "PIN / JETON",
    "history.generated_receipt": "Reçu généré",
    "history.loading": "Chargement de l’historique...",
    "history.activity": "Activité",
    "history.financial_overview": "Aperçu financier",
    "history.total_spent": "Total dépensé",
    "history.lifetime_spending": "Dépenses cumulées",
    "history.transactions": "Transactions",
    "history.success_rate": "Taux de réussite",
    "history.spending_analysis": "Analyse des dépenses",
    "history.top": "Top",
    "history.na": "N/A",
    "history.no_data": "Pas de données",
    "history.search_transactions": "Rechercher des transactions...",
    "history.filter.all": "Tous",
    "history.filter.deposit": "Dépôt",
    "history.no_transactions_found": "Aucune transaction trouvée",

    "auth.alert.account_created": "Compte créé ! Vérifiez votre e‑mail pour confirmer.",
    "auth.alert.reset_link_sent": "Lien de réinitialisation envoyé ! Vérifiez votre e‑mail.",
    "auth.alert.generic_error": "Une erreur s’est produite.",
    "auth.title.login": "Bon retour",
    "auth.title.signup": "Rejoignez la famille",
    "auth.title.reset": "Réinitialiser le mot de passe",
    "auth.button.sign_in": "Se connecter",
    "auth.button.create_account": "Créer un compte gratuit",
    "auth.button.send_reset": "Envoyer le lien",
    "auth.swipe_explore": "Glissez pour explorer",
    "auth.secure_access": "Accès sécurisé",
    "auth.reset_hint": "Saisissez votre e‑mail pour recevoir le lien.",
    "auth.full_name": "Nom complet",
    "auth.email": "Adresse e‑mail",
    "auth.password": "Mot de passe",
    "auth.forgot_password": "Mot de passe oublié ?",
    "auth.back_to_login": "Retour à la connexion",
    "auth.switch_to_signup": "Nouveau ici ? Ouvrir un compte",
    "auth.switch_to_login": "Déjà un compte ? Se connecter",
    "auth.bank_grade_security": "Sécurité de niveau bancaire",

    "profile.updated": "Profil mis à jour !",
    "profile.update_failed": "Échec de la mise à jour. Vérifiez la connexion.",
    "profile.fill_all_fields": "Veuillez remplir tous les champs",
    "profile.passwords_no_match": "Les nouveaux mots de passe ne correspondent pas",
    "profile.password_min": "Le mot de passe doit contenir au moins 6 caractères",
    "profile.incorrect_current_password": "Mot de passe actuel incorrect",
    "profile.password_updated": "Mot de passe mis à jour !",
    "profile.password_update_failed": "Échec de la mise à jour du mot de passe",
    "profile.personal_details": "Détails personnels",
    "profile.name_phone": "Nom et téléphone",
    "profile.phone_number": "Numéro de téléphone",
    "profile.save_changes": "Enregistrer les modifications",
    "profile.appearance": "Apparence",
    "profile.dark_mode": "Mode sombre",
    "profile.light_mode": "Mode clair",
    "profile.security": "Sécurité",
    "profile.change_password": "Changer le mot de passe",
    "profile.current_password": "Mot de passe actuel",
    "profile.new_password": "Nouveau mot de passe",
    "profile.confirm_new_password": "Confirmer le nouveau mot de passe",
    "profile.update_password": "Mettre à jour le mot de passe",
    "profile.help_support": "Aide et support",
    "profile.contact_us": "Nous contacter",
    "profile.log_out": "Se déconnecter",

    "exam.verify_profile_id": "Vérifier l’ID de profil",
    "exam.purchase_success": "Achat réussi ! Vérifiez le reçu pour le PIN.",
    "exam.purchase_failed": "Échec de l’achat",
    "exam.select_exam": "Sélectionner l’examen",
    "exam.profile_id": "ID de profil",
    "exam.enter_profile_id": "Saisir un ID à 10 chiffres",
    "exam.exam_type": "Type d’examen",
    "exam.quantity": "Quantité",
    "exam.pins": "PINs",
    "exam.purchase": "Acheter",
    "exam.verifying": "Vérification...",
    "exam.verified_id": "ID vérifié",
    "exam.invalid_profile_id": "ID de profil invalide",
    "exam.verification_failed": "Échec de la vérification",

    "airtime_to_cash.proceed_manual": "Procédez au transfert d’airtime vers le numéro admin ci‑dessous.",
    "airtime_to_cash.failed_initiate": "Impossible d’initier",
    "airtime_to_cash.request_initiated": "Demande initiée",
    "airtime_to_cash.complete_transfer": "Veuillez terminer le transfert pour finaliser la demande.",
    "airtime_to_cash.instruction": "Instruction",
    "airtime_to_cash.you_sent": "Vous avez envoyé",
    "airtime_to_cash.you_receive": "Vous recevez",
    "airtime_to_cash.i_transferred": "J’ai transféré",
    "airtime_to_cash.swap_airtime": "Échanger Airtime",
    "airtime_to_cash.sender_mobile": "Numéro mobile de l’expéditeur",
    "airtime_to_cash.conversion_details": "Détails de conversion",
    "airtime_to_cash.amount_to_sell": "Montant à vendre",
    "airtime_to_cash.rate_80": "Taux 80%",
    "airtime_to_cash.funds_after_verification": "Fonds ajoutés au portefeuille après vérification.",
    "airtime_to_cash.note_manual_transfer": "Note : le transfert est manuel. Un numéro admin sera fourni.",
    "airtime_to_cash.proceed_swap": "Procéder à l’échange",
  },
  ha: {
    "app.name": "Swifna",
    "nav.ask_ai": "Tambayi AI",
    "nav.buy": "Saya",
    "nav.history": "Tarihi",
    "nav.me": "Ni",
    "lang.english": "Turanci",
    "lang.yoruba": "Yoruba",
    "lang.igbo": "Igbo",
    "lang.french": "Faransanci",
    "lang.hausa": "Hausa",
    "lang.pidgin": "Pidgin",

    "common.back": "Koma",
    "common.balance": "Ma’auni",
    "common.pay": "Biyaa",
    "common.save": "Ajiye",
    "common.share": "Raba",
    "common.date": "Kwana",
    "common.service": "Sabis",
    "common.ref_id": "ID na nuni",
    "common.desc": "Bayani",
    "common.token": "Token",
    "common.pin": "PIN",
    "common.success": "Nasara",
    "common.failed": "Ya gaza",
    "common.pending": "Ana jira",
    "common.cancel": "Soke",
    "common.close": "Rufe",
    "common.search": "Bincika",
    "common.copy": "Kwafi",
    "common.download": "Zazzage",
    "common.amount": "Kudi",
    "common.select": "Zaɓi",
    "common.recent": "Na baya-bayan nan",
    "common.my_number": "Lambar nawa",
    "common.no_recent_transactions": "Babu ciniki na baya-bayan nan",
    "common.no_recent_activity": "Babu aiki na baya-bayan nan",
    "common.loading": "Ana lodawa",
    "common.save_image": "Ajiye Hoto",
    "common.save_pdf": "Ajiye PDF",
    "common.pay_securely": "Biyaa lafiya",

    "dashboard.available_balance": "Ma’aunin da ake da shi",
    "dashboard.fund": "Cika",
    "dashboard.withdraw": "Cire",
    "dashboard.airtime": "Airtime",
    "dashboard.data": "Data",
    "dashboard.cable": "Cable",
    "dashboard.electricity": "Wutar lantarki",
    "dashboard.exam": "Jarrabawa",
    "dashboard.recharge_pin": "PIN Recharge",
    "dashboard.airtime_to_cash": "Airtime zuwa Kudi",
    "dashboard.swap_airtime_for_cash": "Mayar da Airtime zuwa Kudi",
    "dashboard.recent_activity": "Ayyuka na baya-bayan nan",
    "dashboard.fund_wallet": "Cika Wallet",
    "dashboard.total_amount": "Jimlar kudi",
    "dashboard.generated_by": "Na NaijaPay",
    "dashboard.reference_copied": "An kwafi nuni!",
    "dashboard.receipt_title": "Rasit ɗin ciniki",
    "dashboard.receipt_for": "Rasit don {type} - ₦{amount}",
    "dashboard.error_generating_pdf": "Kuskure wajen ƙirƙirar PDF",
    "dashboard.min_deposit": "Mafi ƙaranci ₦100",
    "dashboard.wallet_funded": "An cika Wallet!",

    "airtime.mobile_number": "Lambar waya",
    "airtime.select_recent": "Zaɓi na baya-bayan nan",
    "airtime.recent_transaction": "Ciniki na baya-bayan nan",
    "airtime.top_up": "Cika",
    "airtime.cashback": "Mayar da kudi",
    "airtime.pay_amount": "Biyaa ₦{amount}",
    "airtime.custom_amount": "Ko shigar da adadi (₦50 - ₦500,000)",
    "airtime.insufficient_balance": "Ba isasshen ma’auni",
    "airtime.sent_success": "An tura Airtime lafiya!",
    "airtime.failed": "Ba a iya sarrafa airtime ba",

    "data.select_recent": "Zaɓi na baya-bayan nan",
    "data.recent_data": "Data na baya-bayan nan",
    "data.no_plans": "Babu tsare-tsare a wannan rukuni.",
    "data.price": "Farashi",
    "data.buy_bundle": "Saya Bundle",
    "data.insufficient_balance": "Ba isasshen ma’auni",
    "data.success_sent": "Nasara! An tura {plan}.",
    "data.transaction_failed": "Ciniki ya gaza",

    "assistant.title": "Mataimakin AI",
    "assistant.subtitle": "A kan layi kowane lokaci",
    "assistant.greeting": "Sannu {name}! 👋 Ni ne Swifna AI naka. Tambaye ni game da ma’auni, cika wallet, ko yadda ake sayen data!",
    "assistant.input_placeholder": "Tambayi wani abu...",
    "assistant.quick.balance": "Duba ma’aunina",
    "assistant.quick.deposit": "Yaya zan cika?",
    "assistant.quick.buy_data": "Saya Data",
    "assistant.quick.who": "Kai wanene?",
    "assistant.offline": "Ina da matsala wajen haɗi yanzu. A sake gwadawa.",

    "data.tab.hot": "Zafi",
    "data.tab.daily": "Kullum",
    "data.tab.weekly": "Mako-mako",
    "data.tab.monthly": "Wata-wata",
    "data.type.all": "Duka",
    "data.type.sme": "SME",
    "data.type.cg": "CG",
    "data.type.gifting": "Kyauta",

    "history.share_not_supported": "Raba baya aiki a wannan burauzar. Za a sauke maimakon.",
    "history.reference_copied_clipboard": "An kwafi nuni zuwa clipboard!",
    "history.receipt": "RASID",
    "history.pin_token": "PIN / TOKEN",
    "history.generated_receipt": "An samar da rasit",
    "history.loading": "Ana loda tarihi...",
    "history.activity": "Aiki",
    "history.financial_overview": "Takaitaccen kuɗi",
    "history.total_spent": "Jimlar kashewa",
    "history.lifetime_spending": "Kashewa na duka",
    "history.transactions": "Ma'amaloli",
    "history.success_rate": "Kashi na nasara",
    "history.spending_analysis": "Binciken kashewa",
    "history.top": "Mafi",
    "history.na": "N/A",
    "history.no_data": "Babu bayanai",
    "history.search_transactions": "Bincika ma'amaloli...",
    "history.filter.all": "Duka",
    "history.filter.deposit": "Ciko",
    "history.no_transactions_found": "Ba a sami ma'amala ba",

    "auth.alert.account_created": "An kirkiri asusu! Duba imel don tabbatarwa.",
    "auth.alert.reset_link_sent": "An aika da linkin sake saita kalmar sirri. Duba imel.",
    "auth.alert.generic_error": "Wani kuskure ya faru.",
    "auth.title.login": "Barka da dawowa",
    "auth.title.signup": "Shiga iyali",
    "auth.title.reset": "Sake saita kalmar sirri",
    "auth.button.sign_in": "Shiga",
    "auth.button.create_account": "Kirkiri asusu kyauta",
    "auth.button.send_reset": "Aika linkin sake saita",
    "auth.swipe_explore": "Ja don bincike",
    "auth.secure_access": "Shiga lafiya",
    "auth.reset_hint": "Shigar da imel domin samun linkin sake saita.",
    "auth.full_name": "Cikakken suna",
    "auth.email": "Imel",
    "auth.password": "Kalmar sirri",
    "auth.forgot_password": "Ka manta kalmar sirri?",
    "auth.back_to_login": "Komawa shiga",
    "auth.switch_to_signup": "Sabon mai amfani? Bude asusu",
    "auth.switch_to_login": "Kana da asusu? Shiga",
    "auth.bank_grade_security": "Tsaro irin na banki",

    "profile.updated": "An sabunta bayanin!",
    "profile.update_failed": "Sabuntawa ta gaza. Duba haɗin.",
    "profile.fill_all_fields": "Cika duk filaye",
    "profile.passwords_no_match": "Sabbin kalmomin sirri ba su dace ba",
    "profile.password_min": "Kalmar sirri aƙalla haruffa 6",
    "profile.incorrect_current_password": "Kalmar sirri ta yanzu ba daidai ba",
    "profile.password_updated": "An sabunta kalmar sirri!",
    "profile.password_update_failed": "Ba a sabunta kalmar sirri ba",
    "profile.personal_details": "Bayanin mutum",
    "profile.name_phone": "Suna & Waya",
    "profile.phone_number": "Lambar waya",
    "profile.save_changes": "Ajiye canje-canje",
    "profile.appearance": "Kallo",
    "profile.dark_mode": "Yanayin duhu",
    "profile.light_mode": "Yanayin haske",
    "profile.security": "Tsaro",
    "profile.change_password": "Canja kalmar sirri",
    "profile.current_password": "Kalmar sirri ta yanzu",
    "profile.new_password": "Sabuwar kalmar sirri",
    "profile.confirm_new_password": "Tabbatar da sabuwar kalmar sirri",
    "profile.update_password": "Sabunta kalmar sirri",
    "profile.help_support": "Taimako & Tallafi",
    "profile.contact_us": "Tuntube mu",
    "profile.log_out": "Fita",

    "exam.verify_profile_id": "Tabbatar da ID",
    "exam.purchase_success": "An saye! Duba rasit don PIN.",
    "exam.purchase_failed": "Sayarwa ta gaza",
    "exam.select_exam": "Zaɓi jarabawa",
    "exam.profile_id": "ID",
    "exam.enter_profile_id": "Shigar da ID lamba 10",
    "exam.exam_type": "Nau'in jarabawa",
    "exam.quantity": "Yawa",
    "exam.pins": "PINs",
    "exam.purchase": "Saya",
    "exam.verifying": "Ana tabbatarwa...",
    "exam.verified_id": "ID an tabbatar",
    "exam.invalid_profile_id": "ID ba daidai ba",
    "exam.verification_failed": "Tabbatarwa ta gaza",

    "airtime_to_cash.proceed_manual": "Ci gaba da tura airtime zuwa lambar admin a ƙasa.",
    "airtime_to_cash.failed_initiate": "Ba a iya farawa ba",
    "airtime_to_cash.request_initiated": "An fara buƙata",
    "airtime_to_cash.complete_transfer": "Kammala tura don kammala buƙata.",
    "airtime_to_cash.instruction": "Umarni",
    "airtime_to_cash.you_sent": "Ka tura",
    "airtime_to_cash.you_receive": "Ka karɓa",
    "airtime_to_cash.i_transferred": "Na tura",
    "airtime_to_cash.swap_airtime": "Canza Airtime",
    "airtime_to_cash.sender_mobile": "Lambar wayar mai tura",
    "airtime_to_cash.conversion_details": "Cikakken bayani",
    "airtime_to_cash.amount_to_sell": "Adadin da za a sayar",
    "airtime_to_cash.rate_80": "Kashi 80%",
    "airtime_to_cash.funds_after_verification": "Za a ƙara kuɗi bayan tabbatarwa.",
    "airtime_to_cash.note_manual_transfer": "Lura: tura hannu ne. Za a ba ka lambar admin.",
    "airtime_to_cash.proceed_swap": "Ci gaba da canzawa",
  },
  ng: {
    "app.name": "Swifna",
    "nav.ask_ai": "Ask AI",
    "nav.buy": "Buy",
    "nav.history": "History",
    "nav.me": "Me",
    "lang.english": "English",
    "lang.yoruba": "Yoruba",
    "lang.igbo": "Igbo",
    "lang.french": "French",
    "lang.hausa": "Hausa",
    "lang.pidgin": "Pidgin",

    "common.back": "Back",
    "common.balance": "Balance",
    "common.pay": "Pay",
    "common.save": "Save",
    "common.share": "Share",
    "common.date": "Date",
    "common.service": "Service",
    "common.ref_id": "Ref ID",
    "common.desc": "Desc",
    "common.token": "Token",
    "common.pin": "PIN",
    "common.success": "Success",
    "common.failed": "Failed",
    "common.pending": "Pending",
    "common.cancel": "Cancel",
    "common.close": "Close",
    "common.search": "Search",
    "common.copy": "Copy",
    "common.download": "Download",
    "common.amount": "Amount",
    "common.select": "Select",
    "common.recent": "Recent",
    "common.my_number": "My Number",
    "common.no_recent_transactions": "No recent transactions",
    "common.no_recent_activity": "No recent activity",
    "common.loading": "Loading",
    "common.save_image": "Save Image",
    "common.save_pdf": "Save PDF",
    "common.pay_securely": "Pay secure",

    "dashboard.available_balance": "Available Balance",
    "dashboard.fund": "Fund",
    "dashboard.withdraw": "Withdraw",
    "dashboard.airtime": "Airtime",
    "dashboard.data": "Data",
    "dashboard.cable": "Cable",
    "dashboard.electricity": "Electricity",
    "dashboard.exam": "Exam",
    "dashboard.recharge_pin": "Recharge Pin",
    "dashboard.airtime_to_cash": "Airtime to Cash",
    "dashboard.swap_airtime_for_cash": "Swap Airtime for Cash",
    "dashboard.recent_activity": "Recent Activity",
    "dashboard.fund_wallet": "Fund Wallet",
    "dashboard.total_amount": "Total Amount",
    "dashboard.generated_by": "Generated by NaijaPay",
    "dashboard.reference_copied": "Reference copied!",
    "dashboard.receipt_title": "Transaction Receipt",
    "dashboard.receipt_for": "Receipt for {type} - ₦{amount}",
    "dashboard.error_generating_pdf": "Error generate PDF",
    "dashboard.min_deposit": "Min ₦100",
    "dashboard.wallet_funded": "Wallet don fund!",

    "airtime.mobile_number": "Phone Number",
    "airtime.select_recent": "Select Recent",
    "airtime.recent_transaction": "Recent Transaction",
    "airtime.top_up": "Top up",
    "airtime.cashback": "Cashback",
    "airtime.pay_amount": "Pay ₦{amount}",
    "airtime.custom_amount": "Or enter amount (₦50 - ₦500,000)",
    "airtime.insufficient_balance": "No balance",
    "airtime.sent_success": "Airtime don send!",
    "airtime.failed": "Airtime no go",

    "data.select_recent": "Select Recent",
    "data.recent_data": "Recent Data",
    "data.no_plans": "No plans for this category.",
    "data.price": "Price",
    "data.buy_bundle": "Buy Bundle",
    "data.insufficient_balance": "No balance",
    "data.success_sent": "Success! {plan} don send.",
    "data.transaction_failed": "Transaction fail",

    "assistant.title": "AI Assistant",
    "assistant.subtitle": "Always Online",
    "assistant.greeting": "Hi {name}! 👋 I be your Swifna AI. Ask me about balance, how to fund wallet, or how to buy data!",
    "assistant.input_placeholder": "Ask me anything...",
    "assistant.quick.balance": "Check my balance",
    "assistant.quick.deposit": "How I go fund?",
    "assistant.quick.buy_data": "Buy Data",
    "assistant.quick.who": "Who you be?",
    "assistant.offline": "Network wahala. Try again.",

    "data.tab.hot": "Hot",
    "data.tab.daily": "Daily",
    "data.tab.weekly": "Weekly",
    "data.tab.monthly": "Monthly",
    "data.type.all": "All",
    "data.type.sme": "SME",
    "data.type.cg": "CG",
    "data.type.gifting": "Gift",

    "history.share_not_supported": "Share no dey work for this browser. I go download instead.",
    "history.reference_copied_clipboard": "Reference don copy to clipboard!",
    "history.receipt": "RECEIPT",
    "history.pin_token": "PIN / TOKEN",
    "history.generated_receipt": "Receipt wey generate",
    "history.loading": "Dey load history...",
    "history.activity": "Activity",
    "history.financial_overview": "Your money overview",
    "history.total_spent": "Total wey you spend",
    "history.lifetime_spending": "All-time spending",
    "history.transactions": "Transactions",
    "history.success_rate": "Success rate",
    "history.spending_analysis": "Spending analysis",
    "history.top": "Top",
    "history.na": "N/A",
    "history.no_data": "No data yet",
    "history.search_transactions": "Search transactions...",
    "history.filter.all": "All",
    "history.filter.deposit": "Deposit",
    "history.no_transactions_found": "No transactions found",

    "auth.alert.account_created": "Account don create! Check your email to confirm.",
    "auth.alert.reset_link_sent": "Reset link don send! Check your email.",
    "auth.alert.generic_error": "Something go wrong.",
    "auth.title.login": "Welcome back",
    "auth.title.signup": "Join the family",
    "auth.title.reset": "Reset password",
    "auth.button.sign_in": "Sign In",
    "auth.button.create_account": "Create free account",
    "auth.button.send_reset": "Send reset link",
    "auth.swipe_explore": "Swipe to explore",
    "auth.secure_access": "Secure access",
    "auth.reset_hint": "Enter your email to get reset link.",
    "auth.full_name": "Full Name",
    "auth.email": "Email Address",
    "auth.password": "Password",
    "auth.forgot_password": "Forgot Password?",
    "auth.back_to_login": "Back to Login",
    "auth.switch_to_signup": "New here? Open account",
    "auth.switch_to_login": "Get account? Log in",
    "auth.bank_grade_security": "Bank-level security",

    "profile.updated": "Profile don update!",
    "profile.update_failed": "Update fail. Check connection.",
    "profile.fill_all_fields": "Abeg fill all fields",
    "profile.passwords_no_match": "New passwords no match",
    "profile.password_min": "Password must be at least 6",
    "profile.incorrect_current_password": "Current password no correct",
    "profile.password_updated": "Password don update!",
    "profile.password_update_failed": "No fit update password",
    "profile.personal_details": "Personal details",
    "profile.name_phone": "Name & Phone",
    "profile.phone_number": "Phone Number",
    "profile.save_changes": "Save changes",
    "profile.appearance": "Appearance",
    "profile.dark_mode": "Dark mode",
    "profile.light_mode": "Light mode",
    "profile.security": "Security",
    "profile.change_password": "Change password",
    "profile.current_password": "Current password",
    "profile.new_password": "New password",
    "profile.confirm_new_password": "Confirm new password",
    "profile.update_password": "Update password",
    "profile.help_support": "Help & Support",
    "profile.contact_us": "Contact us",
    "profile.log_out": "Log out",

    "exam.verify_profile_id": "Verify Profile ID",
    "exam.purchase_success": "Purchase successful! Check receipt for PIN.",
    "exam.purchase_failed": "Purchase fail",
    "exam.select_exam": "Select Exam",
    "exam.profile_id": "Profile ID",
    "exam.enter_profile_id": "Enter 10-digit ID",
    "exam.exam_type": "Exam Type",
    "exam.quantity": "Quantity",
    "exam.pins": "PINs",
    "exam.purchase": "Purchase",
    "exam.verifying": "Dey verify...",
    "exam.verified_id": "Verified ID",
    "exam.invalid_profile_id": "Invalid Profile ID",
    "exam.verification_failed": "Verification fail",

    "airtime_to_cash.proceed_manual": "Proceed transfer airtime manually to admin number below.",
    "airtime_to_cash.failed_initiate": "No fit initiate",
    "airtime_to_cash.request_initiated": "Request don start",
    "airtime_to_cash.complete_transfer": "Abeg complete transfer to finish your cash request.",
    "airtime_to_cash.instruction": "Instruction",
    "airtime_to_cash.you_sent": "You Send",
    "airtime_to_cash.you_receive": "You Receive",
    "airtime_to_cash.i_transferred": "I Don Transfer",
    "airtime_to_cash.swap_airtime": "Swap Airtime",
    "airtime_to_cash.sender_mobile": "Sender phone number",
    "airtime_to_cash.conversion_details": "Conversion details",
    "airtime_to_cash.amount_to_sell": "Amount to sell",
    "airtime_to_cash.rate_80": "80% rate",
    "airtime_to_cash.funds_after_verification": "Funds go enter wallet after verification.",
    "airtime_to_cash.note_manual_transfer": "Note: transfer na manual. We go give you admin number.",
    "airtime_to_cash.proceed_swap": "Proceed to swap",
  },
};

const fallbackLanguage: LanguageCode = "en";

type I18nContextValue = {
  language: LanguageCode;
  setLanguage: (lang: LanguageCode) => void;
  t: (key: string, vars?: Record<string, string | number>) => string;
};

const I18nContext = createContext<I18nContextValue | null>(null);

export const I18nProvider = ({
  language,
  setLanguage,
  children,
}: {
  language: LanguageCode;
  setLanguage: (lang: LanguageCode) => void;
  children: React.ReactNode;
}) => {
  const t = useMemo(() => {
    return (key: string, vars?: Record<string, string | number>) => {
      const langTable = translations[language] || translations[fallbackLanguage];
      const base = langTable[key] ?? translations[fallbackLanguage][key] ?? key;
      if (!vars) return base;
      return Object.keys(vars).reduce((acc, k) => {
        return acc.replace(new RegExp(`\\{${k}\\}`, "g"), String(vars[k]));
      }, base);
    };
  }, [language]);

  return (
    <I18nContext.Provider value={{ language, setLanguage, t }}>
      {children}
    </I18nContext.Provider>
  );
};

export const useI18n = () => {
  const ctx = useContext(I18nContext);
  if (!ctx) {
    throw new Error("useI18n must be used within I18nProvider");
  }
  return ctx;
};
